import React from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles,
  Tooltip,
  IconButton,
  Icon,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Avatar,
} from '@material-ui/core';
import { find } from 'lodash';

import Property from '../../../../../../../../widgets/Property';
import StatusBadge from '../../../../../../../../widgets/StatusBadge';
import styles from './styles';

const useStyles = makeStyles(styles);

const WorkflowStatusComponent = ({
  workflowStatus,
  workflowStatuses,
  statuses,
  previousWorkflowStatus,
  nextWorkflowStatus,
  index,
  onMoveBack,
  onMoveForward,
  onDelete,
  onChangeAssignmentRequiredFlag,
  isExternalEmailCommunicationSent,
  onChangeStatusCommunication,
  showStatusCommunicationModal,
  onNextStatusOptionsChange,
}) => {
  const classes = useStyles();

  const [status = {}]
    = statuses.filter((statusItem) => workflowStatus.statusId === statusItem.id);

  return (
    <Accordion className={classes.card} classes={{ root: classes.accordionRoot }}>
      <AccordionSummary classes={{ content: classes.accordionSummary }} expandIcon={<Icon>expand_more</Icon>}>
        <StatusBadge icon={status.icon} iconColor={status.iconColor} />
        <div className={classes.statusName}>
          {status.name}
          {
            index === 0
              && (
                <Typography variant="caption" className={classes.defaultStartingStatusContainer}>
                  <Icon fontSize="small" color="disabled">info_outlined</Icon>
                  &nbsp;
                  <span>Default starting status</span>
                </Typography>
              )
          }
        </div>
        <div>
          {
            previousWorkflowStatus
              && (
                <Tooltip title="Move Back">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveBack(workflowStatus, previousWorkflowStatus);
                    }}
                    className={`${classes.moveButton} move-backward`}
                  >
                    <Icon>arrow_upward</Icon>
                  </IconButton>
                </Tooltip>
              )
          }

          {
            nextWorkflowStatus
              && (
                <Tooltip title="Move Forward">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveForward(workflowStatus, nextWorkflowStatus);
                    }}
                    className={`${classes.moveButton} move-forward`}
                  >
                    <Icon>arrow_downward</Icon>
                  </IconButton>
                </Tooltip>
              )
          }
          {
            !status.archived
              && (
                <Tooltip title="Remove">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(workflowStatus);
                    }}
                  >
                    <Icon>delete</Icon>
                  </IconButton>
                </Tooltip>
              )
          }
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ListItem>
              <ListItemIcon>
                <Checkbox
                  checked={workflowStatus && workflowStatus.requiredAssignment}
                  value={workflowStatus.requiredAssignment}
                  onChange={() => { onChangeAssignmentRequiredFlag(workflowStatus); }}
                  name="requiredAssignment"
                  color="primary"
                />
              </ListItemIcon>
              <ListItemText primary="Require user assignment at this status" secondary="If enabled, one or more users are required to be assigned when changing the workflow to this status." />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Checkbox
                  checked={isExternalEmailCommunicationSent(workflowStatus)}
                  value={workflowStatus.statusCommunication}
                  onChange={() => {
                    if (isExternalEmailCommunicationSent(workflowStatus)) {
                      onChangeStatusCommunication({
                        ...workflowStatus,
                        statusCommunication: null,
                      });
                    } else {
                      showStatusCommunicationModal(workflowStatus);
                    }
                  }}
                  name="sendEmailCommunication"
                  color="primary"
                />
              </ListItemIcon>
              <ListItemText primary="Send external email communications" secondary="If enabled, an email will be sent to addresses provided in a configured email custom field." />
              {
                isExternalEmailCommunicationSent(workflowStatus)
                  && (
                    <ListItemSecondaryAction>
                      <Tooltip title="Configure">
                        <IconButton
                          variant="text"
                          onClick={() => showStatusCommunicationModal(workflowStatus)}
                        >
                          <Icon>edit</Icon>
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  )
              }
            </ListItem>
          </Grid>
          <Grid item xs={12}>
            <Property label="Select Next Available Statuses" disableGutters>
              <List className={classes.statusList}>
                {
                  workflowStatuses
                    .filter((remainingWorkflowStatus) => remainingWorkflowStatus.statusId !== workflowStatus.statusId)
                    .map((remainingWorkflowStatus) => {
                      const remainingStatus = find(statuses, (availableStatus) => availableStatus.id === remainingWorkflowStatus.statusId);
                      return (
                        <ListItem
                          key={`${remainingStatus.id}-${remainingWorkflowStatus.id}`}
                          dense
                          button
                          className={classes.listItem}
                          onClick={() => { onNextStatusOptionsChange(workflowStatus, remainingStatus.id); }}
                        >
                          <Avatar
                            alt={remainingStatus.name}
                            style={{
                              color: remainingStatus.iconColor,
                              width: 30,
                              height: 30,
                            }}
                          >
                            <Icon style={{ fontSize: 18 }}>
                              {remainingStatus.icon}
                            </Icon>
                          </Avatar>
                          <ListItemText primary={remainingStatus.name} classes={{ root: classes.listItemText }} />
                          <ListItemSecondaryAction classes={{ root: classes.listItemAction }}>
                            <Checkbox
                              checked={
                                !!workflowStatus.nextStatusOptions
                                  && workflowStatus.nextStatusOptions.filter((id) => id === remainingStatus.id).length > 0
                              }
                              onChange={() => { onNextStatusOptionsChange(workflowStatus, remainingStatus.id); }}
                              color="primary"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })
                }
              </List>
            </Property>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

WorkflowStatusComponent.propTypes = {
  workflowStatus: PropTypes.object,
  workflowStatuses: PropTypes.array,
  statuses: PropTypes.array,
  nextWorkflowStatus: PropTypes.object,
  previousWorkflowStatus: PropTypes.object,
  index: PropTypes.number,
  onMoveBack: PropTypes.func,
  onMoveForward: PropTypes.func,
  onDelete: PropTypes.func,
  onChangeAssignmentRequiredFlag: PropTypes.func,
  isExternalEmailCommunicationSent: PropTypes.func,
  onChangeStatusCommunication: PropTypes.func,
  showStatusCommunicationModal: PropTypes.func,
  onNextStatusOptionsChange: PropTypes.func,
};

WorkflowStatusComponent.defaultProps = {
  workflowStatus: {},
  workflowStatuses: [],
  statuses: [],
  nextWorkflowStatus: undefined,
  previousWorkflowStatus: undefined,
  index: -1,
  onMoveBack: () => {},
  onMoveForward: () => {},
  onDelete: () => {},
  onChangeAssignmentRequiredFlag: () => {},
  isExternalEmailCommunicationSent: () => {},
  onChangeStatusCommunication: () => {},
  showStatusCommunicationModal: () => {},
  onNextStatusOptionsChange: () => {},
};

export default WorkflowStatusComponent;
